'use client';

import cx from 'classnames';
import { MapPin } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import {
  getJellyImageUrl,
  lotMapImage
} from '~/constants/dealership/testDriveCenter/assets';
import type { LotCarsData } from '~/contracts/dealership';
import {
  getMakeLine,
  getSpecLine,
  getZoneLabel
} from '~/utilities/dealership/lotMap';
import useLotMapSelection from '../hooks/useLotMapSelection';
import styles from '../TestDriveCenter.module.css';

const section = 'Test Drive Center - Lot Map';

export default function TestDriveCenterLotMap({
  lotData
}: {
  lotData: LotCarsData;
}) {
  const {
    hasCars,
    inertDots,
    make,
    makeChips,
    mapDots,
    railCars,
    railRef,
    selectCar,
    selectedCar,
    selectedVon,
    setMake,
    stageRef,
    wrapRef
  } = useLotMapSelection(lotData);

  const [inViewRef, inView] = useInView({
    threshold: 0.12,
    triggerOnce: true
  });

  const selectedAlt = selectedCar
    ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model} ${selectedCar.trim}`
    : '';

  return (
    <section
      className="relative overflow-hidden bg-[var(--cvna-navy)] scroll-mt-64"
      id="map"
    >
      <div className="relative mx-auto max-w-[1320px] px-[clamp(20px,4vw,56px)] pb-[clamp(72px,10vh,120px)] pt-[clamp(60px,8vh,104px)] max-[920px]:pb-[clamp(28px,4vh,40px)] max-[920px]:pt-[clamp(36px,5vh,56px)]">
        <div className={cx(styles.eyebrow, styles.animIn)}>The playground</div>
        <h2
          className={cx(
            styles.animIn,
            'mb-16 max-w-[16ch] font-[family-name:var(--font-display)] text-[clamp(36px,5.4vw,84px)] font-black uppercase leading-[0.9] tracking-[-0.02em] text-white'
          )}
        >
          See the whole lineup
        </h2>
        <p
          className={cx(
            styles.animIn,
            'mb-[26px] max-w-[440px] text-[clamp(16px,1.4vw,19px)] font-normal leading-[1.6] text-[rgba(255,255,255,0.72)]'
          )}
        >
          The whole playground, mapped. Tap any car to preview it, then come
          find it on site.
        </p>
        <div className={cx(styles.animIn, 'flex flex-wrap gap-[10px]')}>
          {makeChips.length > 1 &&
            makeChips.map(chip => (
              <button
                key={chip.value}
                className={cx(styles.chip, {
                  [styles.chipActive]: chip.value === make
                })}
                data-adobe-id={`lot-map-filter-${chip.value.toLowerCase()}`}
                data-analytics-id={`${section} - Filter ${chip.label}`}
                data-testid={`test-drive-center-chip-${chip.value.toLowerCase()}`}
                onClick={() => setMake(chip.value)}
                type="button"
              >
                {chip.label}
                <span className={styles.chipCount}>{chip.count}</span>
              </button>
            ))}
        </div>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-[clamp(20px,4vw,56px)] pb-[clamp(64px,9vh,104px)]">
        {hasCars ? (
          <>
            <div
              ref={node => {
                wrapRef.current = node;
                inViewRef(node);
              }}
              className={styles.mapWrap}
            >
              <div
                ref={stageRef}
                className={styles.mapStage}
                style={{ backgroundImage: `url('${lotMapImage}')` }}
              >
                {/* Keyed by make so changing the filter replays the dot drop without
                reloading the map image. */}
                <div key={make} className="absolute inset-0">
                  {inertDots.map(dot => (
                    <div
                      key={dot.von}
                      className={styles.mapDotInert}
                      style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
                    />
                  ))}
                  {mapDots.map((dot, index) => (
                    <button
                      key={dot.von}
                      aria-label={dot.title}
                      className={cx(styles.mapDot, {
                        [styles.mapDotDrop]: inView,
                        [styles.mapDotSelected]: dot.selected
                      })}
                      data-adobe-id="lot-map-car"
                      data-analytics-id={`${section} - ${dot.title}`}
                      data-testid={`test-drive-center-dot-${dot.von}`}
                      onClick={() => selectCar(dot.von)}
                      style={{
                        animationDelay: inView
                          ? `${(index * 0.012).toFixed(3)}s`
                          : undefined,
                        left: `${dot.x}%`,
                        top: `${dot.y}%`
                      }}
                      title={dot.title}
                      type="button"
                    />
                  ))}
                </div>
              </div>
              <div aria-hidden className={styles.mapVignette} />
            </div>

            {selectedCar && (
              <div className={styles.mapDetail}>
                <div key={selectedVon} className={styles.mapDetailSwap}>
                  <div className="flex aspect-[17/10] items-center justify-center overflow-hidden">
                    <img
                      alt={selectedAlt}
                      className="block h-full w-full object-contain"
                      src={getJellyImageUrl(selectedCar.imgPath)}
                    />
                  </div>
                  <div className="px-[22px] pb-[22px] pt-20">
                    <div className="mb-[9px] text-[11px] font-bold uppercase leading-none tracking-[0.16em] text-[var(--cvna-blue-bright)]">
                      {getMakeLine(selectedCar)}
                    </div>
                    <h3 className="mb-[7px] font-[family-name:var(--font-display)] text-[27px] font-black uppercase leading-[0.96] tracking-[-0.01em] text-white">
                      {selectedCar.model}
                    </h3>
                    <div className="mb-4 text-[15px] font-semibold leading-[1.3] text-white">
                      {selectedCar.trim}
                    </div>
                    <div className="mb-16 text-[13.5px] font-normal leading-[1.5] text-[rgba(255,255,255,0.62)]">
                      {getSpecLine(selectedCar)}
                    </div>
                    <span className="inline-flex items-center gap-[7px] rounded-full border border-[rgba(255,255,255,0.16)] bg-[rgba(255,255,255,0.08)] px-[14px] py-[9px] text-[12.5px] font-semibold leading-none text-white">
                      <MapPin color="var(--cvna-blue-bright)" size={14} />
                      {getZoneLabel(selectedCar)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div ref={railRef} className={styles.carRail}>
              {railCars.map(carItem => (
                <button
                  key={carItem.von}
                  className={cx(styles.railCard, {
                    [styles.railCardSelected]: carItem.selected
                  })}
                  data-adobe-id="lot-map-rail-car"
                  data-analytics-id={`${section} - Rail ${carItem.model}`}
                  data-testid={`test-drive-center-rail-${carItem.von}`}
                  data-von={carItem.von}
                  onClick={() => selectCar(carItem.von)}
                  type="button"
                >
                  <div className="flex aspect-[17/10] items-center justify-center overflow-hidden">
                    <img
                      alt={carItem.alt}
                      className="block h-full w-full object-contain"
                      loading="lazy"
                      src={getJellyImageUrl(carItem.imgPath)}
                    />
                  </div>
                  <div className="px-[13px] pb-[13px] pt-[11px]">
                    <div className="mb-[6px] text-[9.5px] font-bold uppercase leading-none tracking-[0.14em] text-[var(--cvna-blue-bright)]">
                      {carItem.makeLine}
                    </div>
                    <div className="mb-4 font-[family-name:var(--font-display)] text-[17px] font-black uppercase leading-[0.98] tracking-[-0.01em] text-white">
                      {carItem.model}
                    </div>
                    <div className="text-[12px] font-medium leading-[1.3] text-[rgba(255,255,255,0.62)]">
                      {carItem.trim}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="flex min-h-[clamp(280px,40vh,420px)] items-center justify-center rounded-[16px] border border-[rgba(255,255,255,0.12)] bg-[var(--cvna-map-panel)] px-24 text-center text-[clamp(15px,1.4vw,18px)] font-medium leading-[1.5] text-[rgba(255,255,255,0.7)]">
            The lineup is updating — check back in a moment.
          </div>
        )}
      </div>
    </section>
  );
}
